import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import classnames from 'classnames/bind';

import { Button } from '@components';
import { Icon } from 'gd-design-library';
import { CartContext } from 'utils';
import styles from './CartButton.module.scss';

const CN = 'cart-button';
const iconClassName = `${CN}--icon`;
const badgeClassName = `${CN}--badge`;
const badgeForwardAnimationClassName = `${badgeClassName}--animation-forward`;
const badgeReverseAnimationClassName = `${badgeClassName}--animation-reverse`;

const BADGE_ANIMATION_FORWARD = 'forward' as const;
const BADGE_ANIMATION_REVERSE = 'reverse' as const;

const cx = classnames.bind(styles);

interface CartButtonProps {
  onCartClick: () => void;
  onClose?: () => void;
}

export const CartButton: React.FC<CartButtonProps> = ({ onCartClick, onClose }) => {
  const { cart } = useContext(CartContext);
  const quantity = cart?.totalLineItemQuantity ?? 0;

  const previousQuantityRef = useRef<number | undefined>(undefined);
  const [ badgeKey, setBadgeKey ] = useState(0);
  const [ badgeAnimationDirection, setBadgeAnimationDirection ] = useState<
    typeof BADGE_ANIMATION_FORWARD | typeof BADGE_ANIMATION_REVERSE | null
  >(null);
  const [ pendingExitQuantity, setPendingExitQuantity ] = useState<number | null>(null);

  useEffect(() => {
    const previous = previousQuantityRef.current;

    if (quantity <= 0) {
      if (previous !== undefined && previous > 0) {
        setPendingExitQuantity(previous);
        setBadgeAnimationDirection(BADGE_ANIMATION_REVERSE);
        setBadgeKey(k => k + 1);
      }
      previousQuantityRef.current = 0;

      return;
    }

    setPendingExitQuantity(null);

    if (previous === undefined) {
      previousQuantityRef.current = quantity;

      return;
    }

    if (previous === quantity) {
      return;
    }

    setBadgeAnimationDirection(
      quantity > previous ? BADGE_ANIMATION_FORWARD : BADGE_ANIMATION_REVERSE,
    );
    setBadgeKey(k => k + 1);
    previousQuantityRef.current = quantity;
  }, [ quantity ]);

  const displayQuantity = quantity > 0 ? quantity : pendingExitQuantity ?? 0;
  const showBadge = displayQuantity > 0;
  const activeForwardAnimation = badgeAnimationDirection === BADGE_ANIMATION_FORWARD;
  const activeReverseAnimation = badgeAnimationDirection === BADGE_ANIMATION_REVERSE;

  const handleBadgeAnimationEnd = useCallback((): void => {
    setPendingExitQuantity(null);
    setBadgeAnimationDirection(null);
  }, []);

  return (
    <Button
      isIcon
      variant='inherit'
      className={ cx(CN) }
      onClick={ (): void => {
        onClose?.();
        onCartClick();
      } }
      aria-label='Open shopping cart'
    >
      <Icon name='shoppingBag' width={ 28 } height={ 28 } className={ cx(iconClassName) } />
      {showBadge && (
        <span
          key={ badgeKey }
          className={ cx(
            badgeClassName,
            activeForwardAnimation && badgeForwardAnimationClassName,
            activeReverseAnimation && badgeReverseAnimationClassName,
          ) }
          onAnimationEnd={ handleBadgeAnimationEnd }
        >
          { displayQuantity }
        </span>
      )}
    </Button>
  );
};
